/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Divider,
  Backdrop,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import ExportPDFButton from 'src/common/components/ExportPDFButton';
import { EncounterSession } from 'src/common/types/EncounterSession';
import { useNotes } from 'src/florence/hooks/useNotes';
import { initNumberOfResultsFilter } from 'src/florence/utils/initNumberOfResults';

import {
  usePrefetch,
  useRefreshEncounterSessionMutation,
} from '../../../common/apis/FlorenceApi';
import { ReactComponent as CheckmarkIcon } from '../../../common/assets/icons/checkmark_icon_blue.svg';
import { SaveSheet, SheetStatus } from '../../../common/types/SaveSheet';
import ConfirmFinalizePopup from '../../components/ConfirmFinalizePopup/ConfirmFinalizePopup';
import FiltersContainer from '../../components/FiltersContainer/FiltersContainer';
import SearchResultsContainer from '../../components/SearchResultsContainer/SearchResultsContainer';
import { useCodes } from '../../hooks/useCodes';
import CCIAlertBottomBar from '../EditSheetScreen/CCIAlertBottomBar';

import './SmartLaunchTabClinicNotes.scss';

type Props = {
  initialEncounterSession: EncounterSession;
};

const SmartLaunchTabClinicNotes: React.FC<Props> = ({
  initialEncounterSession,
}) => {
  const [encounterSession, setEncounterSession] = useState(
    initialEncounterSession
  );

  const [triggerEncounterRefresh, { isLoading: encounterIsRefreshing }] =
    useRefreshEncounterSessionMutation();

  // filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [limit, setLimit] = useState<10 | 15 | 25 | 10000>(
    initNumberOfResultsFilter(encounterSession.sheet?.sheetCodes?.length ?? 0)
  );

  // ui
  const [isSaving, setIsSaving] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);

  // misc
  const getModifiers = usePrefetch('getModifiers');
  const [hasMultiplePrimaryCodes, setHasMultiplePrimaryCodes] = useState(false);

  // note and codes
  const {
    currentNote,
    finalizeNote,
    updateNote,
    changeClinicNoteStatus,
    updateNoteText,
    refreshNote,
  } = useNotes(encounterSession.sheet ? [encounterSession.sheet] : []);

  const {
    codes,
    selectedCodes,
    refetchCodes,
    isLoading,
    filtersLength,
    selectModifiers,
    selectMultiplier,
    applyFilters,
    clearFilters,
    clinicResponse,
    selectCode,
    selectAllCodes,
    clearSelectedCodes,
    addManualCode,
    incorrectCodeCombinations,
    flagCode,
    updateICD10,
    suggestedICD10,
    AIOutput,
  } = useCodes(
    currentNote,
    currentNote?.scanText ?? '',
    'scan',
    limit,
    updateNote,
    true,
    false
  );

  // Run on page load
  useEffect(() => {
    getModifiers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAll = (select: boolean) => {
    if (select) selectAllCodes();
    else clearSelectedCodes();
  };

  const onSaveNote = async (showConfirmationMessage = false) => {
    const sheetToSave = {
      ...currentNote,
      sheetCodes: selectedCodes,
      clinicNoteResults: clinicResponse,
      aiOutput: AIOutput,
    };
    if (!sheetToSave) return;

    setIsSaving(true);
    await updateNote(sheetToSave as SaveSheet, showConfirmationMessage);
    setIsSaving(false);

    await onClickRefresh();
  };

  const updateClinicNoteStatus = async (status: string) => {
    if (!currentNote) return;
    setIsSaving(true);

    clearSelectedCodes();
    await changeClinicNoteStatus(currentNote, status, true);
    setIsSaving(false);

    refetchCodes();
  };

  const onFinalizeSheet = async () => {
    if (currentNote?.clinicNote) return;

    setIsSaving(true);
    setFinalizeOpen(false);

    const opNote = {
      ...currentNote,
      sheetCodes: selectedCodes,
      aiOutput: AIOutput,
    };
    if (!opNote) return;

    // Save the note first in case the user selected more codes before saving

    setIsSaving(true);
    setFinalizeOpen(false);
    await updateNote(opNote as SaveSheet, false);
    await finalizeNote(opNote as SaveSheet);
    setIsSaving(false);
  };

  // this is for smart launch where it's possible that a user changes generative content without the note being saved first
  // and this would result in saving the note without the CPT code(s)
  const updateNoteGenerativeContent = async (
    note: SaveSheet,
    showConfirmationMessage: boolean
  ) =>
    new Promise<void>((resolve) => {
      updateNote(
        { ...note, sheetCodes: selectedCodes } as SaveSheet,
        showConfirmationMessage
      ).finally(resolve);
    });

  const onClickRefresh = async () => {
    const result = await triggerEncounterRefresh(
      encounterSession.encounterId
    ).unwrap();

    setEncounterSession(result);
    refreshNote(result.sheet);
    refetchCodes();
  };

  useEffect(() => {
    setHasMultiplePrimaryCodes(
      selectedCodes.filter((code) => code.isPrimaryCode).length > 1
    );
  }, [selectedCodes]);

  const expandableText = (title: string, text: string) => {
    return (
      <Accordion
        sx={{
          backgroundColor: 'rgba(126, 178, 199, 0.14)',
          boxShadow: 'none',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>{title}</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingTop: '0px',
          }}
        >
          <pre
            style={{
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              flexGrow: 1,
              position: 'relative',
              fontFamily: 'SFProDisplay-Medium',
              color: '#002145B2',
              fontSize: '16px',
              lineHeight: 1.4,
              height: '100%',
              margin: '0px',
            }}
          >
            {/* SECURITY: render EHR note text as plain text, never as HTML.
                This was previously dangerouslySetInnerHTML, which let any
                markup in an EHR-sourced note (e.g. <img src=x onerror=...>
                in a patient-reported chief complaint) execute as stored XSS
                in the app origin and read the Cognito tokens in localStorage.
                The <pre> above already has whiteSpace: 'pre-wrap', so the raw
                \n newlines in the note render the same as before. */}
            {text}
          </pre>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box
      className='responsive-padding light-blue-background'
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: '100%',
      }}
    >
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1300,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        open={filtersOpen || finalizeOpen}
      >
        {filtersOpen && (
          <FiltersContainer
            onClickApply={applyFilters}
            onClickClose={() => setFiltersOpen(false)}
            onClickCancelAll={clearFilters}
          />
        )}
        {finalizeOpen && (
          <ConfirmFinalizePopup
            onFinalize={onFinalizeSheet}
            onClickClose={() => {
              setFinalizeOpen(false);
            }}
          />
        )}
      </Backdrop>
      <Container
        maxWidth={false}
        sx={{
          textAlign: 'center',
        }}
      >
        <Stack spacing={'30px'}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '24px',
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '24px',
              }}
            >
              {(encounterSession.patient?.firstName ||
                encounterSession.patient?.lastName) && (
                <Box className='patient-box'>
                  <Typography
                    fontSize='16px'
                    color='rgba(82, 86, 98, 0.60)'
                    fontWeight={500}
                  >
                    Patient
                  </Typography>
                  <Typography color='darkBlue' fontSize='18px' fontWeight={700}>
                    {encounterSession.patient.firstName}{' '}
                    {encounterSession.patient.lastName}
                  </Typography>
                </Box>
              )}

              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  marginLeft: '30px',
                  marginRight: '30px',
                  display: { xs: 'none', md: 'block' },
                }}
              />

              {encounterSession.patient?.mrn && (
                <Box className='patient-box'>
                  <Typography
                    fontSize='16px'
                    color='rgba(82, 86, 98, 0.60)'
                    fontWeight={500}
                  >
                    Patient ID
                  </Typography>
                  <Typography color='darkBlue' fontSize='18px' fontWeight={700}>
                    {encounterSession.patient.mrn}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
              }}
            >
              {(currentNote?.clinicNote ||
                currentNote?.status === SheetStatus.FINALIZED) && (
                <ExportPDFButton isDisabled={isLoading} note={currentNote} />
              )}

              {currentNote?.status !== SheetStatus.FINALIZED &&
                !currentNote?.clinicNote && (
                  <Button
                    disabled={
                      isLoading ||
                      isSaving ||
                      selectedCodes.length === 0 ||
                      encounterIsRefreshing
                    }
                    sx={{
                      borderRadius: '48px',
                      height: '48px',
                      px: '24px',
                    }}
                    onClick={() => setFinalizeOpen(true)}
                    variant='outlined'
                    startIcon={<CheckmarkIcon />}
                  >
                    <Typography color='darkBlue'>Finalize</Typography>
                  </Button>
                )}
              <LoadingButton
                onClick={() => onClickRefresh()}
                disabled={isLoading || isSaving || encounterIsRefreshing}
                loading={encounterIsRefreshing}
                disableElevation
                color='darkBlue'
                variant='outlined'
                sx={{
                  borderRadius: '48px',
                  height: '48px',
                  px: '24px',
                }}
              >
                <Typography color='darkBlue'>Refresh</Typography>
              </LoadingButton>
              <Button
                onClick={() => onSaveNote(true)}
                disabled={isLoading || isSaving || encounterIsRefreshing}
                disableElevation
                color='primary'
                variant='contained'
                sx={{
                  borderRadius: '48px',
                  height: '48px',
                  px: '24px',
                }}
              >
                <Typography color='white'>Save & Write to EHR</Typography>
              </Button>
            </Box>
          </Box>
          <Divider />

          <Typography
            color='darkBlue'
            fontSize='18px'
            lineHeight='18px'
            fontWeight={700}
            textAlign='left'
          >
            Clinic Note
          </Typography>

          <Box className='athena-text-sections'>
            {encounterSession.notes?.assessment_notes?.text &&
              expandableText(
                'Assessment',
                encounterSession.notes.assessment_notes.text
              )}

            {encounterSession.notes?.reason_notes?.text &&
              expandableText(
                'Reason',
                encounterSession.notes.reason_notes.text
              )}

            {encounterSession.notes?.physical_exam_notes?.text &&
              expandableText(
                'Physical Exam',
                encounterSession.notes.physical_exam_notes.text
              )}

            {encounterSession.notes?.hpi_notes?.text &&
              expandableText('HPI', encounterSession.notes.hpi_notes.text)}

            {encounterSession.notes?.procedure_note?.text &&
              expandableText(
                'Procedure',
                encounterSession.notes.procedure_note.text
              )}

            {encounterSession.notes?.review_of_system_notes?.text &&
              expandableText(
                'Review of System',
                encounterSession.notes.review_of_system_notes.text
              )}

            {encounterSession.notes?.appointment_type &&
              expandableText(
                'Appointment Type',
                encounterSession.notes.appointment_type
              )}

            {encounterSession.notes?.family_hx &&
              expandableText(
                'Family History',
                encounterSession.notes.family_hx
              )}

            {encounterSession.notes?.social_hx &&
              expandableText(
                'Social History',
                encounterSession.notes.social_hx
              )}

            {encounterSession.notes?.surgical_hx &&
              expandableText(
                'Surgical History',
                encounterSession.notes.surgical_hx
              )}

            {encounterSession.notes?.medical_hx &&
              expandableText(
                'Medical History',
                encounterSession.notes.medical_hx
              )}

            {encounterSession.notes?.orders &&
              expandableText('Orders', encounterSession.notes.orders)}

            {encounterSession.notes?.procedure_doc &&
              expandableText(
                'Procedure Documentation',
                encounterSession.notes.procedure_doc
              )}

            {encounterSession.notes?.procedure_times &&
              expandableText(
                'Procedure Times',
                encounterSession.notes.procedure_times
              )}

            {encounterSession.notes?.procedure_roles &&
              expandableText(
                'Procedure Roles',
                encounterSession.notes.procedure_roles
              )}

            {encounterSession.notes?.vitals &&
              expandableText('Vitals', encounterSession.notes.vitals)}

            {encounterSession.notes?.problems &&
              expandableText('Problems', encounterSession.notes.problems)}

            {encounterSession.notes?.medication &&
              expandableText('Medication', encounterSession.notes.medication)}

            {encounterSession.notes?.allergies &&
              expandableText('Allergies', encounterSession.notes.allergies)}
          </Box>

          {currentNote && (
            <SearchResultsContainer
              hasSelectedAll={
                codes.length !== 0 &&
                codes.filter((c) => !c.selected).length === 0
              }
              selectedAll={selectedAll}
              selectCode={selectCode}
              selectMultiplier={selectMultiplier}
              selectModifiers={selectModifiers}
              limit={limit}
              changeLimit={setLimit}
              searchResults={codes}
              currentNote={currentNote}
              clinicResponse={clinicResponse}
              searchType={'scan'}
              searchText={currentNote?.scanText ?? ''}
              refreshTextSearch={(text) => {
                if (currentNote) {
                  updateNoteText(currentNote, text);
                }
              }}
              refetchCodes={refetchCodes}
              selectedFilterIcon={() => setFiltersOpen(!filtersOpen)}
              filtersLength={filtersLength}
              isLoading={isLoading || isSaving || encounterIsRefreshing}
              updateClinicNoteStatus={updateClinicNoteStatus}
              clearSelectedCodes={clearSelectedCodes}
              flagCode={flagCode}
              updateNote={updateNoteGenerativeContent}
              allowNoteTextEditing={false}
              isSmartLaunch={true}
              feeScheduleCodes={encounterSession.feeScheduleCodes}
              addManualCode={addManualCode}
              selectedCodes={selectedCodes}
              hasMultiplePrimaryCodes={hasMultiplePrimaryCodes}
              updateICD10={updateICD10}
              suggestedICD10={suggestedICD10}
            />
          )}
        </Stack>
      </Container>
      <CCIAlertBottomBar
        open={incorrectCodeCombinations && !encounterIsRefreshing && !isLoading}
      />
    </Box>
  );
};

export default SmartLaunchTabClinicNotes;
